import { DiscussionActivity } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { useDiscussionEntryHeader } from '../../hooks/useDiscussionEntryHeader';

import type { User } from '@/types/directory/User';
import type { ObjectiveComment } from '@/types/objectives';

interface ObjectiveStructureUpdateCardProps {
    comment: ObjectiveComment;
    /** One sentence per difference, worded by the server; the caller narrows on `comment.kind`. */
    lines: string[];
    /** Whoever saved, resolved from `comment.authorId` by the caller. */
    author?: User;
}

/**
 * One automatic entry in the "Discussion" thread: the server appended it when someone saved the
 * objective's structure. A trace, not a message -- "Alice edited the objective" over one line per
 * difference, so a lowered target, a dropped key result or a removed participant reads plainly.
 * The sentences come already written, in the reader's language: the server words them once, for
 * this card and for the daily summary email alike.
 */
export default function ObjectiveStructureUpdateCard({
    comment,
    lines,
    author,
}: ObjectiveStructureUpdateCardProps) {
    const { t } = useTranslation();
    // Several edits can land the same day: the time of day tells them apart.
    const header = useDiscussionEntryHeader({
        author,
        date: comment.createdAt,
        withTimeOfDay: true,
    });

    return (
        <DiscussionActivity
            {...header}
            label={t('objectives.detail.discussion.journal.structure.entryLabel')}
            dataTestId={`objective-discussion-structure-update-${comment.id}`}
        >
            {t('objectives.detail.discussion.journal.structure.edited')}
            <ul className={'mt-1 list-disc ps-4'}>
                {lines.map((line, index) => (
                    // A save lists each difference once and the entry never reorders.
                    <li key={`${line}-${index}`}>{line}</li>
                ))}
            </ul>
        </DiscussionActivity>
    );
}
